import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";

const COPY = {
  monthly: {
    desc: "Get listed & visible across PA.",
    bullets: [
      "30-day free trial",
      "Live menu sync",
      "Pickup/delivery badges",
      "Cancel anytime",
    ],
  },
  annual: {
    desc: "Save ~20% with annual billing.",
    bullets: [
      "Everything in Monthly",
      "Annual savings (2 months free)",
      "Priority support",
      "Trial then $1,428/yr",
    ],
    highlight: true,
  },
  featured_addon: {
    desc: "Featured placement on results & home.",
    bullets: [
      "Top-of-results placement",
      "Featured badge",
      "Add to any plan",
      "Cancel anytime",
    ],
  },
};

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const { user } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/billing/packages").then((r) => setPackages(r.data.packages));
  }, []);

  const subscribe = async (pkgId) => {
    if (!user) {
      navigate(`/dispensary/signup?next=/pricing&pkg=${pkgId}`);
      return;
    }
    setLoadingPkg(pkgId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: pkgId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoadingPkg(null);
    }
  };

  return (
    <main className="container-narrow py-14">
      <div className="text-center max-w-2xl mx-auto">
        <div className="eyebrow mb-3">For dispensaries</div>
        <h1 className="font-serif text-5xl mb-4">
          Simple plans. <span className="italic text-primary">Real customers.</span>
        </h1>
        <p className="text-foreground/70">
          Try Bud Link free for 30 days. After your trial, choose monthly or
          save with annual. Featured placement is an optional add-on.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {packages.map((p) => {
          const copy = COPY[p.id] || {};
          const highlight = copy.highlight;
          const monthly = p.id === "annual" ? p.amount / 12 : p.amount;
          return (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`relative rounded-3xl border bg-card p-8 flex flex-col ${
                highlight
                  ? "border-primary shadow-[0_0_0_4px_rgba(122,139,125,0.12)]"
                  : "border-border"
              }`}
            >
              {highlight && (
                <span className="absolute -top-3 left-8 pill pill-primary text-[10px]">
                  <Sparkles className="w-3 h-3" /> Most popular
                </span>
              )}
              <div className="eyebrow mb-2">{p.label}</div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-serif text-5xl">${monthly.toFixed(0)}</span>
                <span className="text-muted-foreground text-sm">
                  /{p.id === "annual" ? "mo (billed yearly)" : "mo"}
                </span>
              </div>
              {p.id === "annual" && (
                <div className="text-xs text-muted-foreground mb-3">
                  ${p.amount.toFixed(2)} billed once per year
                </div>
              )}
              {p.id === "featured_addon" && (
                <div className="text-xs text-muted-foreground mb-3">
                  Add-on · stacks on monthly or annual
                </div>
              )}
              <p className="text-sm text-foreground/70 mt-1">{copy.desc}</p>

              <ul className="mt-6 space-y-3 text-sm flex-1">
                {copy.bullets?.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`pricing-subscribe-${p.id}`}
                disabled={loadingPkg === p.id}
                onClick={() => subscribe(p.id)}
                className={`mt-8 h-12 rounded-full ${
                  highlight
                    ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                    : "bg-foreground hover:bg-foreground/90 text-background"
                }`}
              >
                {loadingPkg === p.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : p.trial_days > 0 ? (
                  `Start ${p.trial_days}-day free trial`
                ) : (
                  "Add to plan"
                )}
              </Button>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Powered by Stripe · Cancel anytime · No setup fees
      </div>
    </main>
  );
}
